import { useEffect, useState, type RefObject } from 'react';
import { createPortal } from 'react-dom';

const OVERRIDE_SLOT_ATTR = 'data-aspectly-neo-duration-slot';

interface NeoEditorDurationOverrideProps {
  containerRef: RefObject<HTMLElement | null>;
  durationMs?: number;
  onDurationChange?: (ms: number) => void;
}

export function NeoEditorDurationOverride({
  containerRef,
  durationMs,
  onDurationChange,
}: NeoEditorDurationOverrideProps) {
  const [slot, setSlot] = useState<HTMLDivElement | null>(null);
  const [prevDurationMs, setPrevDurationMs] = useState(durationMs);
  const [localDuration, setLocalDuration] = useState<string>(() =>
    durationMs != null ? String(Math.round(durationMs / 1000)) : ''
  );

  if (durationMs !== prevDurationMs) {
    setPrevDurationMs(durationMs);
    setLocalDuration(durationMs != null ? String(Math.round(durationMs / 1000)) : '');
  }

  const handleDurationBlur = () => {
    if (!onDurationChange) return;
    const val = parseInt(localDuration, 10);
    if (!Number.isNaN(val) && val > 0) {
      onDurationChange(val * 1000);
    } else {
      onDurationChange(60000);
      setLocalDuration('60');
    }
  };

  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;

    let mountedSlot: HTMLDivElement | null = null;
    let originalTextNode: Text | null = null;
    let originalContent = '';
    let hiddenElement: HTMLElement | null = null;
    let hiddenDisplay = '';
    let hiddenTextNode: Text | null = null;
    let hiddenTextContent = '';

    const mountSlot = () => {
      if (mountedSlot?.isConnected) return true;

      // Find the deepest element containing the time pattern
      const elements = root.querySelectorAll('*');
      let deepestMatch: HTMLElement | null = null;
      const timeRegex = /\d+:\d+.*\/\s*\d+:\d+/;

      for (let i = 0; i < elements.length; i++) {
        const el = elements[i] as HTMLElement;
        // Ignore our own slot
        if (el.hasAttribute(OVERRIDE_SLOT_ATTR)) continue;
        
        if (timeRegex.test(el.textContent || '')) {
          deepestMatch = el;
        }
      }

      if (!deepestMatch) return false;

      // We found the container.
      const next = document.createElement('div');
      next.setAttribute(OVERRIDE_SLOT_ATTR, '');
      next.className = 'rve:inline-flex rve:items-center rve:ml-1';
      next.style.pointerEvents = 'auto'; // Ensure it can be clicked even if parent is disabled

      // Check if deepestMatch is a text-only element (no element children)
      if (deepestMatch.children.length === 0) {
        // It's a single text node inside an element
        const targetNode = deepestMatch.firstChild as Text;
        if (targetNode && targetNode.nodeType === Node.TEXT_NODE) {
          const text = targetNode.nodeValue || '';
          const parts = text.split('/');
          if (parts.length === 2) {
            originalContent = text;
            originalTextNode = targetNode;
            targetNode.nodeValue = parts[0] + '/ ';
            deepestMatch.appendChild(next);
            mountedSlot = next;
            setSlot(next);
            return true;
          }
        }
      } else {
        // It has children. The rightmost text is the total time.
        let foundSlash = false;
        let rightChildElement: HTMLElement | null = null;
        let rightChildText: Text | null = null;

        for (let i = 0; i < deepestMatch.childNodes.length; i++) {
          const child = deepestMatch.childNodes[i];
          // We mark foundSlash if the child text includes /
          if (child.textContent?.includes('/')) {
            foundSlash = true;
            
            // If the slash is actually in a text node and the right time is ALSO in this text node!
            // (e.g. `<span>0:00.00</span> / 1:00:00`)
            if (child.nodeType === Node.TEXT_NODE && /\d+:\d+/.test(child.textContent.split('/')[1] || '')) {
              // We found the total time in the SAME text node as the slash!
              rightChildText = child as Text;
              break;
            }
            continue;
          }
          if (foundSlash && /\d+:\d+/.test(child.textContent || '')) {
            if (child.nodeType === Node.ELEMENT_NODE) {
              rightChildElement = child as HTMLElement;
            } else if (child.nodeType === Node.TEXT_NODE) {
              rightChildText = child as Text;
            }
            break;
          }
        }

        if (rightChildElement) {
          hiddenElement = rightChildElement;
          hiddenDisplay = rightChildElement.style.display;
          rightChildElement.style.display = 'none';
          if (rightChildElement.nextSibling) {
            deepestMatch.insertBefore(next, rightChildElement.nextSibling);
          } else {
            deepestMatch.appendChild(next);
          }
          mountedSlot = next;
          setSlot(next);
          return true;
        } else if (rightChildText) {
          hiddenTextNode = rightChildText;
          hiddenTextContent = rightChildText.nodeValue || '';
          const slashIdx = hiddenTextContent.indexOf('/');
          if (slashIdx !== -1) {
            // Keep everything up to the slash + space
            rightChildText.nodeValue = hiddenTextContent.substring(0, slashIdx) + '/ ';
          } else {
             rightChildText.nodeValue = '';
          }
          if (rightChildText.nextSibling) {
            deepestMatch.insertBefore(next, rightChildText.nextSibling);
          } else {
            deepestMatch.appendChild(next);
          }
          mountedSlot = next;
          setSlot(next);
          return true;
        }
      }
      return false;
    };

    if (!mountSlot()) {
      // It might not be in the DOM yet
    }

    const observer = new MutationObserver(() => {
      mountSlot();
    });
    observer.observe(root, { childList: true, subtree: true, characterData: true });

    return () => {
      observer.disconnect();
      mountedSlot?.remove();
      if (originalTextNode && originalContent) {
        originalTextNode.nodeValue = originalContent;
      }
      if (hiddenElement) {
        hiddenElement.style.display = hiddenDisplay;
      }
      if (hiddenTextNode && hiddenTextContent) {
        hiddenTextNode.nodeValue = hiddenTextContent;
      }
      setSlot(null);
    };
  }, [containerRef]);

  if (!slot) return null;

  return createPortal(
    <input
      type="number"
      min={1}
      className="rve:h-6 rve:w-12 rve:rounded rve:border rve:border-input rve:bg-transparent rve:px-1 rve:text-xs rve:font-medium rve:text-foreground rve:shadow-sm focus-visible:rve:outline-none focus-visible:rve:ring-1 focus-visible:rve:ring-ring"
      value={localDuration}
      onChange={(e) => setLocalDuration(e.target.value)}
      onBlur={handleDurationBlur}
      title="Edit length (seconds)"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onMouseUp={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === 'Enter') {
          e.currentTarget.blur();
        }
      }}
      onKeyUp={(e) => e.stopPropagation()}
      onKeyPress={(e) => e.stopPropagation()}
    />,
    slot,
  );
}
